import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  type OnDestroy,
} from '@angular/core';
import { ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatTabGroup } from '@angular/material/tabs';
import { AppointmentSchedulingFacade } from '@principle-theorem/ng-appointment/store';
import {
  ContactNumberControls,
  CurrentBrandScope,
  CurrentScopeFacade,
  FeeScheduleSelectorBloc,
  OrganisationService,
  TagType,
} from '@principle-theorem/ng-principle-shared';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import {
  InputSearchFilter,
  MOMENT_DATEPICKER_PROVIDERS,
  toSearchStream,
  TrackByFunctions,
  TypedFormControl,
  type TypedFormArray,
} from '@principle-theorem/ng-shared';
import {
  Address,
  Brand,
  FeeSchedule,
  IPatientDetails,
  isPatientDetails,
  Staffer,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  FeeScheduleScope,
  GENDERS,
  IFeeSchedule,
  IFeeScheduleGroup,
  IMohSchool,
  IPatientMetadata,
  IPatientMetadataConfiguration,
  NZ_SCHOOLS,
  PATIENT_STATUSES,
  PatientMetadataValue,
  SYSTEM_PATIENT_PROFILE_METADATA_CONFIGURATIONS,
  type Gender,
  type IContactNumber,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ISuggestedPatientData,
  type ITag,
  type PatientOptionalField,
  type PatientStatus,
} from '@principle-theorem/principle-core/interfaces';
import { PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE } from '@principle-theorem/principle-core/features';
import {
  filterUndefined,
  isPathChanged$,
  isSameRef,
  multiSortBy$,
  multiSwitchMap,
  nameSorter,
  Region,
  snapshot,
  toNamedDocument,
  type CollectionReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { get, isNil, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { type Moment } from 'moment-timezone';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { EditPatientFormGroup } from './edit-patient-form';

@Component({
  selector: 'pr-edit-patient-form',
  exportAs: 'prEditPatientForm',
  templateUrl: './edit-patient-form.component.html',
  styleUrls: ['./edit-patient-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  standalone: false,
})
export class EditPatientFormComponent implements OnDestroy, OnInit {
  private _onDestroy$ = new Subject<void>();
  trackByPractitioner = TrackByFunctions.ref<INamedDocument<IStaffer>>();
  trackByPractice = TrackByFunctions.ref<INamedDocument<IPractice>>();
  trackByGender = TrackByFunctions.variable<Gender>();
  trackByStatus = TrackByFunctions.variable<PatientStatus>();
  trackByIndex = TrackByFunctions.index();
  today: Moment = moment();
  patientForm = new EditPatientFormGroup();
  practitioners$: Observable<INamedDocument<IStaffer>[]>;
  practices$: Observable<INamedDocument<IPractice>[]>;
  feeScheduleGroups$: Observable<IFeeScheduleGroup[]>;
  feeScheduleDefault$: Observable<string>;
  staleFeeSchedule$: Observable<INamedDocument<IFeeSchedule> | undefined>;
  genders: Gender[] = GENDERS;
  statuses: PatientStatus[] = PATIENT_STATUSES;
  tagCol$: Observable<CollectionReference<ITag>>;
  contactNumberControls: ContactNumberControls;
  patient$ = new BehaviorSubject<WithRef<IPatient> | undefined>(undefined);
  suggestedPatientData$ = new BehaviorSubject<
    ISuggestedPatientData | undefined
  >(undefined);
  patientSelectorCtrl = new TypedFormControl<WithRef<IPatient> | string>('');
  nameMatHint =
    'Preferred name example: Leonardo (Leo) Dicaprio. (Leo) will be removed from patient correspondence.';
  formReady$ = new BehaviorSubject(false);
  depositsEnabled$: Observable<boolean>;
  newPatientName$ = new ReplaySubject<string | undefined>(1);
  tagType = TagType.Patient;
  errorStateMatcher = new ShowOnDirtyErrorStateMatcher();
  region = Region;
  schoolSearch: InputSearchFilter<IMohSchool>;
  metadataConfigurations = SYSTEM_PATIENT_PROFILE_METADATA_CONFIGURATIONS;
  patientMetadataControls$: Observable<
    {
      configuration: IPatientMetadataConfiguration;
      control: TypedFormControl<PatientMetadataValue | undefined>;
    }[]
  >;

  @Output() submitted = new EventEmitter<Partial<IPatientDetails>>();
  @Output() patientDetailsChanges = new EventEmitter<boolean>();
  @Input() tabGroup: MatTabGroup;
  @Input() emitPatientDataOnUpdate = false;

  @Input()
  set patient(patient: WithRef<IPatient> | undefined) {
    this.patient$.next(patient ?? undefined);
  }

  @Input()
  set newPatientName(name: string | undefined) {
    if (name) {
      this.newPatientName$.next(name);
    }
  }

  constructor(
    private _brandScope: CurrentBrandScope,
    private _organisation: OrganisationService,
    private _schedulingFacade: AppointmentSchedulingFacade,
    private _scope: CurrentScopeFacade,
    private _featureFlags: FeatureFlagsService
  ) {
    this.depositsEnabled$ = this._brandScope.depositsEnabled$;
    const organisation$ =
      this._organisation.organisation$.pipe(filterUndefined());
    this.contactNumberControls = new ContactNumberControls(this.contactNumbers);

    combineLatest([this.patient$, this.newPatientName$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([patient, name]) => {
        if (!patient && name) {
          this.patientForm.controls.name.setValue(name, { emitEvent: false });
          this.patientSelectorCtrl.setValue(name, { emitEvent: false });
        }
      });

    this.practitioners$ = this._brandScope.model$.pipe(
      switchMap((brand?) => {
        if (!brand) {
          return of([]);
        }
        return Staffer.practitionersByBrand$(brand);
      }),
      toINamedDocuments()
    );

    this.practices$ = this._brandScope.model$.pipe(
      switchMap((brand) => (brand ? Brand.practices$(brand) : of([]))),
      toINamedDocuments()
    );

    const organisationFeeSchedules$: Observable<IFeeScheduleGroup> =
      organisation$.pipe(
        switchMap((organisation) =>
          FeeSchedule.all$(organisation).pipe(
            map((schedules) => ({
              name: 'Organisation Schedules',
              scope: FeeScheduleScope.Organisation,
              schedules,
            }))
          )
        )
      );

    const practiceFeeSchedules$: Observable<IFeeScheduleGroup[]> =
      this.practices$.pipe(
        multiSortBy$(nameSorter()),
        multiSwitchMap((practice) =>
          FeeSchedule.all$(practice).pipe(
            map((schedules) => ({
              name: practice.name,
              scope: FeeScheduleScope.Practice,
              schedules,
            }))
          )
        )
      );

    this.tagCol$ = this._organisation.brand$.pipe(
      filterUndefined(),
      map((brand) => Brand.patientTagCol(brand))
    );

    this.patientForm.valueChanges
      .pipe(
        debounceTime(300),
        withLatestFrom(organisation$),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([formData, { region }]) =>
        this.suggestedPatientData$.next({
          name: formData.name,
          dateOfBirth: formData.dateOfBirth,
          contactNumbers: formData.contactNumbers,
          region,
        })
      );

    this.patientSelectorCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((patient) => {
        if (!isNil(patient) && !isString(patient)) {
          this.selectPatient(patient);
          return;
        }
        this.patientForm.controls.name.setValue(patient);
      });

    this.patientForm.valueChanges
      .pipe(
        filter(() => this.emitPatientDataOnUpdate),
        debounceTime(300),
        withLatestFrom(this.patient$),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([_, patient]) => {
        const patientDetails = this._getPatientDetails(patient);
        if (this.patientForm.valid && isPatientDetails(patientDetails)) {
          this.submitted.emit(patientDetails);
          return;
        }
        this._schedulingFacade.clearPatientDetails();
      });

    const currentPractice$ = combineLatest([
      this._scope.currentPractice$,
      this._schedulingFacade.practice$,
    ]).pipe(
      map(
        ([currentPractice, schedulingPractice]) =>
          schedulingPractice ?? currentPractice
      )
    );
    const practiceFeatureEnabled$ = this._featureFlags.isFeatureEnabled$(
      PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE
    );

    const feeScheduleDefault$ = combineLatest([
      organisation$,
      currentPractice$,
    ]).pipe(
      switchMap(([org, practice]) => {
        return FeeSchedule.getPreferredOrDefault(
          org,
          practice?.settings?.defaultFeeSchedule
        );
      })
    );

    const allFeeScheduleGroups$ = combineLatest([
      organisationFeeSchedules$,
      practiceFeeSchedules$,
    ]).pipe(
      map(([orgFeeSchedules, practiceFeeSchedules]) => [
        orgFeeSchedules,
        ...practiceFeeSchedules,
      ])
    );

    const preferredControl = this.patientForm.controls.preferredFeeSchedule;
    const preferredFeeSchedule$: Observable<
      INamedDocument<IFeeSchedule> | undefined
    > = preferredControl.valueChanges.pipe(
      startWith(preferredControl.value),
      map((schedule) => schedule ?? undefined)
    );
    const feeScheduleBloc = new FeeScheduleSelectorBloc<
      INamedDocument<IFeeSchedule>
    >(
      allFeeScheduleGroups$,
      practiceFeatureEnabled$,
      feeScheduleDefault$,
      preferredFeeSchedule$
    );
    this.feeScheduleGroups$ = feeScheduleBloc.groups$;
    this.feeScheduleDefault$ = feeScheduleBloc.autoLabel$;
    this.staleFeeSchedule$ = feeScheduleBloc.staleSchedule$;

    const schoolSearchStream = toSearchStream(
      this.patientForm.moh.controls.school as TypedFormControl<IMohSchool>
    );
    this.schoolSearch = new InputSearchFilter(
      of(NZ_SCHOOLS),
      schoolSearchStream,
      ['schoolName']
    );

    this.patientMetadataControls$ = this.patient$.pipe(
      take(1),
      map((patient) => patient?.metadata),
      map((metadata) =>
        SYSTEM_PATIENT_PROFILE_METADATA_CONFIGURATIONS.map((configuration) => {
          const value = get(metadata, configuration.key) as
            | PatientMetadataValue
            | undefined;
          const control = new TypedFormControl<
            PatientMetadataValue | undefined
          >(value);
          this.patientForm.metadata.addControl(configuration.key, control);

          return {
            configuration,
            control,
          };
        })
      )
    );
  }

  ngOnInit(): void {
    this.patient$
      .pipe(isPathChanged$('ref.path'), takeUntil(this._onDestroy$))
      .subscribe((patient) => {
        this.formReady$.next(false);
        this.patientForm.reset({ emitEvent: false });
        this.patientSelectorCtrl.reset(undefined, { emitEvent: false });
        this.contactNumberControls.reset({ emitEvent: false });
        this.suggestedPatientData$.next(undefined);

        if (patient) {
          this.contactNumberControls.initContactNumberControls(patient, {
            emitEvent: false,
          });

          this.patientForm.patchValue(
            {
              ...patient,
              address: Address.normaliseAddress(patient),
              email: patient.email?.trim(),
            },
            { emitEvent: this.emitPatientDataOnUpdate }
          );
          this.patientForm.moh.patchValue(
            { school: patient.metadata?.moh?.school },
            { emitEvent: this.emitPatientDataOnUpdate }
          );
        }
        this.formReady$.next(true);
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async submit(): Promise<void> {
    if (this.patientForm.invalid || !this.patientForm.dirty) {
      return;
    }
    const patient = await snapshot(this.patient$);
    const patientDetails = this._getPatientDetails(patient);
    this.submitted.emit(patientDetails);
  }

  selectPatient(patient: WithRef<IPatient>): void {
    this._schedulingFacade.selectPatient(patient);
    this.tabGroup.selectedIndex = 0;
    this.patientSelectorCtrl.reset();
  }

  clearPatient(): void {
    this.patientSelectorCtrl.reset();
    this._schedulingFacade.clearPatient();
  }

  isSelectedNamedDocument(
    namedDocumentOption: INamedDocument,
    selectedNamedDocument?: INamedDocument
  ): boolean {
    if (!selectedNamedDocument) {
      return false;
    }
    return isSameRef(selectedNamedDocument, namedDocumentOption);
  }

  get contactNumbers(): TypedFormArray<IContactNumber> {
    return this.patientForm.controls
      .contactNumbers as TypedFormArray<IContactNumber>;
  }

  isRequired$(field: PatientOptionalField): Observable<boolean> {
    return this._organisation.brand$.pipe(
      map((brand) => {
        const requiredFields = brand?.settings.patient?.requiredFields ?? [];
        return requiredFields.includes(field);
      })
    );
  }

  schoolDisplayFn(school?: IMohSchool): string {
    return school ? school.schoolName : '';
  }

  private _getPatientDetails(
    patient?: WithRef<IPatient>
  ): Partial<IPatientDetails> & Partial<IPatient> {
    const data = this.patientForm.getRawValue();
    const address = data.address;
    const school = data.nz.moh.school;
    const metadata: IPatientMetadata = patient
      ? { ...patient.metadata, ...data.metadata, address, moh: { school } }
      : { address, moh: { school } };

    return {
      ...data,
      address: data.address?.address ?? '',
      metadata,
      preferredFeeSchedule: data.preferredFeeSchedule
        ? toNamedDocument(data.preferredFeeSchedule)
        : undefined,
    };
  }
}
